import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { activityLog } from "@/features/activityLog";
import { announceTokensPurchased } from "@/shared/lib/tokensPurchased";
import { loadPlanWallet } from "../model/planStore";
import { fmtDate, planCopy } from "../model/planCopy";
import { takePendingPlan } from "../model/usePlanCheckout";
import { PlanToast } from "./SpecialistPlanBar";

/** Метка возврата со Stripe после оплаты пакета (site_server payments.PLAN_PAID_PARAM). */
export const PLAN_PAID_PARAM = "plan_paid";

const handled = new Set<string>();

/** Для тестов: забыть обработанные оплаты. */
export const resetPlanPurchaseRoot = () => handled.clear();

const SHOW_MS = 5000;

/**
 * Возврат со Stripe после оплаты пакета — на любой странице (App): сервер
 * выдал пакет ещё до переадресации (возврат, вебхук — один пакет на заказ) и
 * вернул туда, откуда покупали, с ?plan_paid=1&session_id=…. Здесь — убрать
 * метки, перечитать кошелёк мимо кэша и сказать «Пакет на 3 месяца — до …»;
 * страницы с балансом перечитывают его по тому же сигналу, что и после токенов.
 */
export const PlanPurchaseRoot: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [text, setText] = useState<string | null>(null);
    const timer = useRef<number | undefined>(undefined);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paid = params.get(PLAN_PAID_PARAM);
        if (paid === null) return;
        const sid = params.get("session_id") || "";
        params.delete(PLAN_PAID_PARAM);
        params.delete("session_id");
        const rest = params.toString();
        navigate(
            { pathname: location.pathname, search: rest ? `?${rest}` : "", hash: location.hash },
            { replace: true, state: location.state },
        );
        if (paid !== "1" || handled.has(sid)) return;
        handled.add(sid);
        const months = takePendingPlan();
        const lang = location.pathname.split("/")[1] || "ru";
        void loadPlanWallet({ fresh: true, force: true })
            .then((w) => {
                activityLog.track("plan_paid", { months: months ?? undefined, mode: w?.mode });
                announceTokensPurchased();
                if (!w || w.mode !== "plans") return;
                const c = planCopy(lang);
                const until = w.questions?.active ? w.questions.until : w.search?.until;
                const date = fmtDate(until, lang);
                if (!date) return;
                setText(c.paidToast(months ? c.term(months) : null, date));
                window.clearTimeout(timer.current);
                timer.current = window.setTimeout(() => setText(null), SHOW_MS);
            })
            .catch(() => undefined);
        // Только смена адреса: navigate и state — не повод начинать заново.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    return text ? <PlanToast text={text} /> : null;
};
